import React, { useCallback, useContext, useEffect, useMemo, useState, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { EpoJobDetailContext } from '../EpoJobDetails';
import DataTable from '../../../DataTable';
import { ExternalLinkAlt, Trash } from '@styled-icons/fa-solid';
import { Tab, Tabs } from 'react-bootstrap';
import { useDataTableHandle } from '../../../DataTable/DataTable';
import { getEPOInvoice } from '../../../../api/epo';
import {
  getEPO,
  getNewLoad,
  putEPO,
  deleteEPODocument,
  restoreEPODocument,
  postVatRestrictions,
} from '../../../apiCalls';
import { Input } from '../../../Input/Input';
import GridTotals from '../../../GridTotals';
import { StoreContext } from '../../../Store';
import './excel.css';
import MyModal from './MyModal';
import Spreadsheet, { useSpreadsheetHandle } from '../../../Spreadsheet/Spreadsheet';
import RowNumberColumn from '../../../Spreadsheet/RowNumberColumn/RowNumberColumn';
import { cleanseChedRef } from '../../../../utils/stringValidation';
import DocsNavControls from './DocsNavControls';
import BulkOperations from './BulkOperations';
import { useParams, useNavigate } from 'react-router-dom';
import { useSecurity } from '../../../Security/security';
import restartSVG from '../../../../images/restart.svg';
import { DocViewer } from '../DocViewers';
import AuthModal from '../../../Newload/AuthModal';
import { getHeaderValues } from '../../../Newload/QAChecklistTab/utils';
import { downloadAllEPOFiles } from '../../../../utils/download';
import DownloadButton from '../../../Buttons/DownloadButton';

import ExciseModal from '../../../ExciseModal/ExciseModal';
import { CODES_WITH_EXCISE } from '../../../ExciseModal/constants';
import { getExciseOptions } from '../../../../api/excise';

export default function EpoJobInvoices() {
  const {
    jobDetails,
    openedInvoice,
    setOpenedInvoice,
    tab,
    invoiceDetails,
    setInvoiceDetails,
    setInvoiceId,
    loadIsReadOnly,
  } = useContext(EpoJobDetailContext);

  const store = useContext(StoreContext);
  const navigate = useNavigate();
  const { validateRole } = useSecurity();

  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    async function fetchData() {
      getNewLoad('vat').then((data) => {
        store.VatOptions[1](data);
      });

      getExciseOptions().then((data) => {
        store.exciseOptions[1](data);
      });
    }
    fetchData();
  }, []);
  const fetchData = async () => {
    if (tab === 'invoices' || tab === 'items') {
      const getEPOTab = await getEPO(jobDetails.newLoadId, 'invoicestab');
      if (getEPOTab) {
        setInvoiceDetails(getEPOTab);
      }
    } else return;
  };
  useEffect(() => {
    fetchData();
  }, [tab]);

  const handleDelete = async (invoice) => {
    const data = await deleteEPODocument(invoice?.invoiceId);
    if (data[0].completed === 'Deleted') {
      fetchData();
    } else {
      const params = new URLSearchParams();
      params.append('authError', 'Sorry, something went wrong while performing that action.');
      navigate.push('/EpoJobDetails?' + params.toString());
    }
  };

  const handleRestore = async (event, invoice) => {
    event.preventDefault();
    const data = await restoreEPODocument(invoice?.invoiceId);
    if (data[0].completed === 'Restored') {
      fetchData();
    } else {
      const params = new URLSearchParams();
      params.append('authError', 'Sorry, something went wrong while performing that action.');
      navigate('/EpoJobDetails?' + params.toString());
    }
  };

  const viewInvoice = (invoice) => {
    if (invoice.Items) {
      invoice.Items.map((row) => {
        if (row.VAT.length === 1 || row.VAT < 4) {
          row.VAT = store.VatOptions[0].find((opt) => parseInt(opt.id) === parseInt(row.VAT))?.data;
        }
        return row;
      });
    }
    setInvoiceId(invoice?.invoiceId);
    setOpenedInvoice(invoice);
  };

  const onDownloadAllFiles = async () => {
    const filename = `${jobDetails.TransferID || 'epo'}-documents.zip`;
    setIsDownloading(true);
    await downloadAllEPOFiles(jobDetails.newLoadId, filename);
    setIsDownloading(false);
  };

  if (!invoiceDetails) return null;

  return (
    <>
      <EpoInvoiceModal
        invoice={openedInvoice}
        onClose={() => setOpenedInvoice(null)}
        sdiSubmitted={store?.sdiSubmitted}
        sfdSubmitted={store?.sfdSubmitted}
        eadSubmitted={store?.eadSubmitted}
      />

      <div>
        <DataTable data={invoiceDetails} className="table-striped table-bordered" idKey="itemId">
          <DataTable.Column colKey="filename" header="Filename" />
          <DataTable.Column colKey="doctype" header="Doc Type" />
          <DataTable.Column colKey="invStatus" header="Status" />
          <DataTable.Column colKey="itemRows" header="Total Rows"></DataTable.Column>
          <DataTable.Column colKey="lastModifiedUser" header="Last Modified By" />
          <DataTable.Column header="View" style={{ height: '1px', padding: 0 }}>
            {(invoice) => (
              <button className="external-table-link" onClick={() => viewInvoice(invoice)}>
                <ExternalLinkAlt width="1.1rem" height="1.1rem" color="#00857d" />
              </button>
            )}
          </DataTable.Column>
          {(validateRole({ department: 'OPS', role: 'SUPERUSER' }) ||
            validateRole({ department: 'QA', role: 'SUPERUSER' })) && (
            <DataTable.Column
              header=""
              style={{ textAlign: 'center', paddingTop: '6px', width: '80px', height: '45px' }}
            >
              {(invoice) =>
                invoice.showIcon &&
                !loadIsReadOnly &&
                (invoice.deleted ? (
                  <button
                    className="reset-btn-filters restore-btn"
                    onClick={(event) => handleRestore(event, invoice)}
                    style={{ border: '0px' }}
                  >
                    <img style={{ color: 'blue' }} src={restartSVG} width="20" alt="Spinning Arrow" />
                  </button>
                ) : (
                  <Trash
                    width="1.1rem"
                    className="invoice-delete-icon"
                    style={{ cursor: 'pointer', color: 'red' }}
                    onClick={() => handleDelete(invoice)}
                  />
                ))
              }
            </DataTable.Column>
          )}
        </DataTable>

        {invoiceDetails?.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <DownloadButton isLoading={isDownloading} onClick={onDownloadAllFiles}>
              Download All
            </DownloadButton>
          </div>
        )}
      </div>
    </>
  );
}

function EpoInvoiceModal({ invoice, onClose, sdiSubmitted, sfdSubmitted, eadSubmitted }) {
  const {
    jobDetails,
    setJobDetails,
    canSave,
    isCustomer,
    invoiceDetails,
    setInvoiceDetails,
    invoiceId,
    setInvoiceId,
    showBulkBtn,
    setServerData,
    setBuffer,
    setCycle,
  } = useContext(EpoJobDetailContext);
  const [localInvoiceData, setLocalInvoiceData] = useState();
  const [error, setError] = useState();
  const [isSaving, setIsSaving] = useState(false);
  const [showItems, setShowItems] = useState(true);
  const [selectedTab, setSelectedTab] = useState('summary');
  const [successText, setSuccessText] = useState(false);
  const spreadsheetHandle = useSpreadsheetHandle();
  const store = useContext(StoreContext);
  const childRef = React.createRef();
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showExciseModal, setShowExciseModal] = useState(false);
  const [vatAuthEntered, setVatAuthEntered] = useState(false);

  const { id } = useParams();

  useEffect(() => {
    setSelectedTab('summary');
  }, []);

  const closeItems = () => {
    const fetchData = async () => {
      const getEPOTab = await getEPO(jobDetails.newLoadId, 'invoicestab');
      if (getEPOTab) {
        setInvoiceDetails(getEPOTab);
      } else return;
    };
    fetchData();
    setSelectedTab('summary');
    setSuccessText(false);
    setCycle(false);
    onClose();
  };

  const mapEPOVat = async (epoItems) => {
    epoItems.map((row) => {
      if (row.VAT.length === 1 || row.VAT < 4) {
        row.VAT = store.VatOptions[0].find((opt) => parseInt(opt.id) === parseInt(row.VAT))?.data;
      }
      return row;
    });
    return epoItems;
  };

  useEffect(() => {
    const fetchData = async () => {
      if (selectedTab === 'items') {
        // invoiceId added for the dynamic nav control
        const getEPOItems = await getEPO(invoiceId, selectedTab + 'tab');
        if (getEPOItems) {
          const updatedItems = await mapEPOVat(getEPOItems);
          if (updatedItems) {
            loadRows(getEPOItems);
          }
        }
      }
    };
    fetchData();
  }, [selectedTab]);

  useEffect(() => {
    invoice?.doctype !== 'Commercial Document' ? setShowItems(false) : setShowItems(true);
    setLocalInvoiceData(invoice);
    if (invoice?.invoiceId) {
      getEPOInvoice(invoice.invoiceId).then((res) => {
        setBuffer(Buffer.from(res[0].invoice.data));
        setCycle(true);
      });
    }
  }, [invoice]);

  const loadRows = (items) => {
    const rows = items.map((i) => [
      { text: i.commcode, itemId: i.itemId },
      { text: i.origin },
      { text: i.commDesc },
      { text: i.packageCount },
      { text: i.packageType },
      { text: i.grossMass },
      { text: i.netMass },
      { text: i.loadValue },
      { text: i.quantity2 },
      { text: i.healthCert },
      { text: i.catchCert },
      { text: i.meursing },
      { text: i.organic },
      { text: i.VAT },
      { text: i.RGR },
      { text: i.preferential },
      { text: i.exciseProductType },
      { text: i.exciseTaxCode },
    ]);
    spreadsheetHandle.current.excelDispatch({ type: 'set', detail: rows });
  };

  const itemDataHandler = useDataTableHandle();
  const handleTabSelect = (eventKey) => {
    if (eventKey === 'summary') {
      setError();
      setSuccessText(false);
    }
    setSelectedTab(eventKey);
  };

  const removeEmptyOrNull = (obj) => {
    Object.keys(obj).forEach(
      (k) =>
        (obj[k] && typeof obj[k] === 'object' && removeEmptyOrNull(obj[k])) ||
        (!obj[k] && obj[k] !== undefined && delete obj[k]) ||
        (typeof obj[k] === 'string' && obj[k].trim() === '' && delete obj[k])
    );
    return obj;
  };

  const handleSave = async () => {
    if (isSaving) return; // ignore double clicks

    setIsSaving(true);
    try {
      await validateHsRows(); // existing logic
    } finally {
      setIsSaving(false);
    }
  };

  const getColumnIndex = (columnName) => {
    const keys = spreadsheetHandle.current.excelState.config?.map((c) => c.key);
    if (!keys) {
      return -1;
    }
    const columnIndex = keys.findIndex((e) => e === columnName);
    return columnIndex;
  };

  const commCodeIndex = getColumnIndex('commcode');
  const productTypeIndex = getColumnIndex('exciseProductType');
  const exciseCodeIndex = getColumnIndex('exciseTaxCode');

  const validateHsRows = async () => {
    setError();
    if (!localInvoiceData.invoiceNumber) {
      setError('Please enter an Invoice number at the top of the page to continue.');
      return;
    }
    if (localInvoiceData.invoiceNumber.length >= 35) {
      setError('Invoice number Cannot exceed 35 characters.');
      return;
    }
    // Collapses any completely empty rows and squeezes non-empty rows together.
    let collapsedRows = [];
    for (const idx in spreadsheetHandle.current.excelState.rows) {
      const row = spreadsheetHandle.current.excelState.rows[idx];

      // Clear the exciseProductType and exciseTaxCode fields if the commodity code is not in the list of codes that require excise.
      const commCode = row[commCodeIndex]?.text;
      const isValidForExcise = CODES_WITH_EXCISE.includes(commCode);
      if (!isValidForExcise) {
        row[productTypeIndex] = { text: '' };
        row[exciseCodeIndex] = { text: '' };
      }

      if (row.some((cell) => cell.text !== '')) {
        collapsedRows.push(row);
      }
    }

    spreadsheetHandle.current.excelDispatch({ type: 'set', detail: collapsedRows }); // update the visual rows to the newly collapsed - needed so error index = shown index.

    // Uses configuration 'keys' to construct array of item objects
    const items = collapsedRows.map((row, row_idx) => {
      const obj = {};
      spreadsheetHandle.current.excelState.config.forEach((col, idx) => {
        const { key } = col;
        obj[key] = row[idx]?.text ?? '';
        if (row[idx]?.itemId !== undefined) {
          obj['itemId'] = row[idx].itemId;
        }
      });
      return obj;
    });

    // Backend doesn't like empty strings or nulls, preferred straight 'undefined'.
    const filteredList = removeEmptyOrNull(items).filter((value) => Object.keys(value).length !== 0);
    try {
      const vatRestrictionsCheck = await postVatRestrictions({
        items: filteredList.map((item) => ({
          ...item,
          itemId: item.itemId <= 0 ? null : item.itemId,
        })),
        consigneeId: null,
        epoId: jobDetails.newLoadId,
        directionId: null,
      });

      if (vatRestrictionsCheck[0].passwordRequired && !vatAuthEntered) {
        setShowAuthModal(true);
      } else {
        const data = await putEPO(
          {
            items: filteredList.map((item) => ({
              ...item,
              itemId: item.itemId <= 0 ? null : item.itemId,
            })),
            invId: invoiceId,
            id: jobDetails.newLoadId,
            invoiceNumber: localInvoiceData.invoiceNumber,
          },
          'Items',
          jobDetails.newLoadId
        );
        //check if data[0].error is true OR if 'RGRvalid' is present in data[0]
        if (
          data[0]?.error === true ||
          'RGRvalid' in data[0] ||
          data[0]?.RGRValid === false ||
          data[0]?.areaMatchValid === false ||
          data[0]?.VATWarnValid === false ||
          data[0]?.VATAutoSet === false ||
          data[0]?.prefAutoSet === false
        ) {
          setSuccessText(true);
          const getEPOItems = await getEPO(invoiceId, 'itemstab');
          if (getEPOItems) {
            const updatedItems = await mapEPOVat(getEPOItems);
            if (updatedItems) {
              loadRows(getEPOItems);
              setHsRowErrors(data);
            }
          }
          const data1 = await getEPO(jobDetails.newLoadId, 'Header');
          if (data1) {
            setJobDetails({ id, ...data1.Parents[0] });
            setServerData({ id, ...data1.Parents[0] });
          }
        } else {
          const newDetails = data;
          const updatedItems = await mapEPOVat(newDetails);
          if (updatedItems) {
            loadRows(newDetails);
            if (Array.isArray(data) && data.length > 0) {
              setHsRowErrors(data);
            }
          }

          setSuccessText(true);
          const data1 = await getEPO(jobDetails.newLoadId, 'Header');
          if (data1) {
            setJobDetails({ id, ...data1.Parents[0] });
            setServerData({ id, ...data1.Parents[0] });
          }
          //Close popup
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const AUTO_TOOLTIP = 'Value auto corrected';

  /* Same structure as CABIE ProductInfo setHsRowErrors; EPO adds VAT/Pref auto-correct (data-auto) and commcodeError. */
  const setHsRowErrors = useCallback(
    (errors) => {
      const sheet = spreadsheetHandle.current;
      if (!Array.isArray(errors) || errors.length === 0 || !sheet?.excelState?.config?.length) return;

      const errorsPresent = errors[0]?.error === true;
      const keys = sheet.excelState.config.map((c) => c.key);

      const readAutoCorrectedValue = (errRow, autoKey) => {
        if (!autoKey) return undefined;
        const autoLower = autoKey.toLowerCase();
        const valueEntry = Object.entries(errRow).find(([k]) => k && k.toLowerCase() === `${autoLower}value`);
        let corrected = valueEntry?.[1];
        if (corrected === undefined || corrected === null || String(corrected).trim() === '') {
          if (autoLower === 'preferentialauto') {
            corrected =
              errRow.prefAutoValue ??
              errRow.PrefAutoValue ??
              errRow.preferentialAutoValue ??
              errRow.PreferentialAutoValue;
          }
        }
        return corrected;
      };

      /** API may set VATAuto / preferentialAuto to true, 1, or e.g. "Value auto corrected". */
      const autoFlagIndicatesCorrection = (raw) =>
        raw === true || raw === 1 || (typeof raw === 'string' && raw.trim() !== '');

      const updateFunc = (prevRows) => {
        for (let y = 0; y < prevRows.length; y++) {
          const row = keys.map((key, idx) => {
            if (idx === 0) {
              if (y < errors.length) {
                prevRows[y][idx].itemId = errors[y].itemId;
              }
            }
            if (y < errors.length) {
              const errRow = errors[y];
              const keyLower = key.toLowerCase();

              const autoEntry = Object.entries(errRow).find(([k]) => k && k.toLowerCase() === `${keyLower}auto`);
              const autoKey = autoEntry?.[0];
              const autoRaw = autoEntry?.[1];
              const hasAuto = autoFlagIndicatesCorrection(autoRaw);

              let nextText = prevRows[y][idx]?.text ?? '';
              if (hasAuto && autoKey) {
                const corrected = readAutoCorrectedValue(errRow, autoKey);
                if (corrected !== undefined && corrected !== null && String(corrected).trim() !== '') {
                  nextText = String(corrected);
                }
              }

              const errCell =
                errorsPresent && Object.entries(errRow).find(([k, v]) => k.toLowerCase() === `${keyLower}error`)?.[1];
              const warnCell =
                !errorsPresent && Object.entries(errRow).find(([k, v]) => k.toLowerCase() === `${keyLower}warn`)?.[1];

              const autoMessageText =
                hasAuto &&
                typeof autoRaw === 'string' &&
                autoRaw.trim() &&
                autoRaw.trim() !== '1' &&
                autoRaw.trim() !== 'true'
                  ? autoRaw.trim()
                  : hasAuto
                    ? AUTO_TOOLTIP
                    : undefined;

              return {
                ...prevRows[y][idx],
                text: nextText,
                error: errCell ? errCell : undefined,
                warn: warnCell ? warnCell : undefined,
                auto: hasAuto ? true : undefined,
                autoMessage: autoMessageText,
              };
            }
            return { ...prevRows[y][idx] };
          });
          const commIndex = keys.indexOf('commcode');
          if (commIndex !== -1 && y < errors.length) {
            row[commIndex] = {
              ...prevRows[y][commIndex],
              error: errors[y].commcodeError,
            };
          }
          prevRows[y] = [...row];
        }
        return [...prevRows];
      };

      sheet.excelDispatch({ type: 'set', detail: updateFunc });
    },
    [spreadsheetHandle]
  );

  const [pageNo, setPageNo] = useState(null);
  const totalDocs = invoiceDetails?.length;

  useEffect(() => {
    if (childRef.current) {
      const pageNumber = childRef.current.getPageNo() + 1;
      setPageNo(pageNumber);
    }
  }, [localInvoiceData?.invoiceId]);

  const handleNext = () => {
    if (childRef.current) {
      childRef.current.loadNextDoc();
      setPageNo((prevNo) => prevNo + 1);
      let doctype = invoiceDetails[pageNo].doctype;
      if (doctype === 'Commercial Document') {
        setInvoiceId(invoiceDetails[pageNo].invoiceId);
      }
      doctype !== 'Commercial Document' ? setShowItems(false) : setShowItems(true);
    }
  };

  const handlePrev = () => {
    if (childRef.current) {
      childRef.current.loadPrevDoc();
      setPageNo((prevNo) => prevNo - 1);
      let doctype = invoiceDetails[pageNo - 2].doctype;
      if (doctype === 'Commercial Document') {
        setInvoiceId(invoiceDetails[pageNo - 2].invoiceId);
      }
      doctype !== 'Commercial Document' ? setShowItems(false) : setShowItems(true);
    }
  };

  const dowloadFile = () => {
    if (childRef.current) {
      childRef.current.dowloadFile();
    }
  };

  const setVatAuthEntryTrig = async () => {
    setShowAuthModal(false);
    setVatAuthEntered(true);
  };

  useEffect(() => {
    if (vatAuthEntered === true) {
      validateHsRows();
    }
  }, [vatAuthEntered]);

  if (!localInvoiceData?.invoiceId) return null;

  const epoTabsModal = () => {
    return (
      <MyModal
        show={localInvoiceData?.invoiceId && !showAuthModal}
        centered
        contentClassName="invoice-modal"
        dialogClassName="superwide-modal"
        onHide={() => null}
        onScroll={() => spreadsheetHandle.current.excelDispatch({ type: 'recalculate' })}
      >
        <MyModal.Header>
          <MyModal.Title>Viewing: {invoiceDetails[pageNo - 1]?.filename}</MyModal.Title>
          {selectedTab === 'summary' && (
            <DocsNavControls
              dowloadFile={dowloadFile}
              handleNext={handleNext}
              pageNo={pageNo}
              handlePrev={handlePrev}
              onClose={closeItems}
              totalDocs={totalDocs}
            />
          )}
        </MyModal.Header>
        <MyModal.Body className="flex-grow-1">
          <Tabs onSelect={handleTabSelect}>
            <Tab title="Summary" eventKey="summary">
              <EpoInvoiceSummary invoiceId={localInvoiceData?.invoiceId} ref={childRef} />
            </Tab>
            {showItems && (
              <Tab title="Items" eventKey="items">
                <EpoInvoiceItems
                  ref={spreadsheetHandle}
                  spreadsheetVisible={selectedTab === 'items'}
                  localInvoiceData={localInvoiceData}
                  itemDataHandler={itemDataHandler}
                  setLocalInvoiceData={setLocalInvoiceData}
                  sdiSubmitted={sdiSubmitted}
                  sfdSubmitted={sfdSubmitted}
                  eadSubmitted={eadSubmitted}
                  showBulkModal={showBulkModal}
                  onBulkClose={() => setShowBulkModal(false)}
                  pageNo={pageNo - 1}
                />
              </Tab>
            )}
          </Tabs>
        </MyModal.Body>
        <MyModal.Footer>
          <div className="d-flex flex-row w-100 py-3 px-3 align-self-center align-items-center justify-content-start gap-3">
            {selectedTab === 'items' && showBulkBtn && (
              <button onClick={() => setShowBulkModal(true)} className="blue-button-teams green-btn">
                Bulk Action
              </button>
            )}
            {selectedTab === 'items' && (
              <button onClick={() => setShowExciseModal(true)} className="blue-button-teams green-btn">
                Excise
              </button>
            )}
          </div>
          <div
            className="d-flex flex-row pt-2 align-items-center justify-content-end"
            style={{ color: '#842029', width: '60%' }}
          >
            {selectedTab === 'items' && error}
          </div>
          {successText && (
            <div className="d-flex flex-row py-4 px-3 gap-3 justify-content-end">
              <span className="text-success bold">Successfully&nbsp;saved.</span>
            </div>
          )}
          <div className="d-flex flex-row py-3 px-3 gap-3 justify-content-end">
            <button
              type="button"
              className="blue-button-teams"
              onClick={closeItems}
              style={{ display: 'flex', backgroundColor: '#ffbe00' }}
            >
              Back
            </button>
            {canSave && !isCustomer && (
              <button
                type="button"
                disabled={isSaving || selectedTab === 'summary' || localInvoiceData.deleted}
                className="blue-button-teams red-btn"
                onClick={handleSave}
              >
                Save
              </button>
            )}
          </div>
        </MyModal.Footer>
      </MyModal>
    );
  };

  const onExciseModalSave = (data) => {
    const updatedRows = [...spreadsheetHandle.current.excelState.rows];
    const exciseIndex = getColumnIndex('exciseTaxCode');
    const productTypeIndex = getColumnIndex('exciseProductType');

    data.forEach((item, idx) => {
      const itemIndex = item.lineNumber - 1;
      if (updatedRows[itemIndex]) {
        updatedRows[itemIndex][exciseIndex].text = item.exciseTaxCode || '';
        updatedRows[itemIndex][productTypeIndex].text = item.exciseProductType || '';
      }
    });

    spreadsheetHandle.current.excelDispatch({
      type: 'set',
      detail: updatedRows,
    });
  };

  return (
    <>
      <AuthModal
        show={showAuthModal && !vatAuthEntered}
        onHide={() => setShowAuthModal(false)}
        onSuccess={setVatAuthEntryTrig}
      />
      <ExciseModal
        items={spreadsheetHandle?.current?.excelState?.rows}
        show={showExciseModal}
        onHide={() => setShowExciseModal(false)}
        onSave={onExciseModalSave}
        commCodeIndex={commCodeIndex}
        productTypeIndex={productTypeIndex}
        exciseCodeIndex={exciseCodeIndex}
        exciseOptions={store.exciseOptions[0]}
        disabled={!canSave || sdiSubmitted[0] === 1 || sfdSubmitted[0] === 1 || eadSubmitted[0] === 1}
      />
      {epoTabsModal()}
    </>
  );
}

const invoiceModalPropType = PropTypes.shape({
  invoiceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  doctype: PropTypes.string,
  invoiceNumber: PropTypes.string,
  deleted: PropTypes.bool,
  Items: PropTypes.array,
  showIcon: PropTypes.bool,
});

EpoInvoiceModal.propTypes = {
  invoice: PropTypes.oneOfType([invoiceModalPropType, PropTypes.oneOf([null])]),
  onClose: PropTypes.func.isRequired,
  sdiSubmitted: PropTypes.array,
  sfdSubmitted: PropTypes.array,
  eadSubmitted: PropTypes.array,
};

const EpoInvoiceSummary = forwardRef((props, ref) => {
  const { invoiceId } = props;
  const { invoiceDetails, fileBuffer, setBuffer, cycle } = useContext(EpoJobDetailContext);
  const [curDocIndex, setCurDocIndex] = useState(invoiceId || null);

  const invoiceIndex = invoiceDetails?.findIndex((item) => item.invoiceId === invoiceId);

  const [curIndex, setCurIndex] = useState(() => invoiceIndex);
  const [pageNo, setPageNo] = useState(curIndex);
  const totalDocs = invoiceDetails?.length;

  useEffect(() => {
    if (curDocIndex && cycle) {
      getEPOInvoice(curDocIndex).then((res) => {
        setBuffer(Buffer.from(res[0].invoice.data));
      });
    }
  }, [curDocIndex]);

  const loadNextDoc = () => {
    if (curIndex < totalDocs) {
      setCurIndex((preIndex) => preIndex + 1);
      setCurDocIndex(invoiceDetails[curIndex + 1]['invoiceId']);
      setPageNo((preNo) => preNo + 1);
    }
  };

  const loadPrevDoc = () => {
    if (curIndex > 0) {
      setCurIndex((prevIndex) => prevIndex - 1);
      setCurDocIndex(invoiceDetails[curIndex - 1]['invoiceId']);
      setPageNo((prevPageNo) => prevPageNo - 1);
    }
  };

  const getPageNo = () => {
    return pageNo;
  };

  // Expose the function to the parent component using useImperativeHandle
  useImperativeHandle(ref, () => ({
    loadNextDoc,
    loadPrevDoc,
    dowloadFile,
    getPageNo,
  }));

  const dowloadFile = () => {
    if (!fileBuffer) return;
    const fileURL = window.URL.createObjectURL(new Blob([fileBuffer]));
    const a = document.createElement('a');
    document.body.appendChild(a);
    a.href = fileURL;
    a.download = invoiceDetails[curIndex].filename;
    a.click();
    setTimeout(() => {
      window.URL.revokeObjectURL(fileURL);
      document.body.removeChild(a);
    }, 0);
  };

  const filetype = invoiceDetails[curIndex].filename.split('.').slice(-1)[0].toLowerCase();

  return (
    <section className="p-2 d-flex flex-column justify-content-around gap-2 mt-3">
      <div
        className="p-3 rounded-3"
        style={{ height: '540px', overflowY: 'auto', border: '2px solid hsl(0, 0%, 75%)', cursor: 'pointer' }}
      >
        <div className="invoice-preview">
          <DocViewer fileBuffer={fileBuffer} filetype={filetype} />
        </div>
      </div>
    </section>
  );
});

EpoInvoiceSummary.propTypes = {
  invoiceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

const EpoInvoiceItems = forwardRef(
  (
    {
      onBulkClose = { onBulkClose },
      localInvoiceData,
      setLocalInvoiceData,
      spreadsheetVisible,
      sdiSubmitted,
      sfdSubmitted,
      eadSubmitted,
      showBulkModal,
      pageNo,
    },
    ref
  ) => {
    const { canSave, jobDetails, setShowBulkBtn, setInvoiceDetails, invoiceDetails, loadIsReadOnly } =
      useContext(EpoJobDetailContext);
    const [netMass, setNetMass] = useState(0);
    const [grossMass, setGrossMass] = useState(0);
    const [itemCount, setItemCount] = useState(0);
    const [totalValue, setTotalValue] = useState(0);
    const [itemRows, setItemRows] = useState(0);
    const [itemHeaders, setItemHeaders] = useState(0);
    const [error, setError] = useState('');

    useEffect(() => {
      const fetchData = async () => {
        const getEPOTab = await getEPO(jobDetails?.newLoadId, 'invoices' + 'tab');
        if (getEPOTab && getEPOTab?.length > 0) {
          setInvoiceDetails(getEPOTab);
        }
      };
      fetchData();
    }, [pageNo]);

    useEffect(() => {
      setLocalInvoiceData((prev) => {
        if (prev.Items?.length > 0) return prev;

        const temp_items = [];
        for (let i = 1; i <= 2; i++) {
          temp_items.push({
            itemId: 0 - i,
            commcode: '',
            origin: '',
            commDesc: '',
            packageCount: undefined,
            packageType: '',
            grossMass: undefined,
            netMass: undefined,
            organic: undefined,
            healthCert: undefined,
            catchCert: undefined,
            meursing: undefined,
            loadValue: undefined,
            quantity2: undefined,
            VAT: undefined,
            RGR: undefined,
            preferential: undefined,
            exciseProductType: undefined,
            exciseTaxCode: undefined,
          });
        }
        return {
          ...prev,
          Items: temp_items,
        };
      });
    }, [setLocalInvoiceData]);

    useEffect(() => {
      const doesRowHaveData = (row, isArray) => {
        const rowAsArray = isArray ? row : Object.values(row);
        return rowAsArray.some((cell) => !!cell.text);
      };

      const sumsFunction = (state) => {
        let headers = [
          { name: 'grossMass', update: setGrossMass },
          { name: 'netMass', update: setNetMass },
          { name: 'loadValue', update: setTotalValue },
          { name: 'packageCount', update: setItemCount },
        ];
        let itemRowsCalc = 0;
        const isArray = Array.isArray(state.rows?.[0]);
        headers = headers.map((obj) => {
          return { ...obj, value: 0, col: isArray ? state.config.findIndex((c) => c.key === obj.name) : undefined };
        });

        state.rows.forEach((row) => {
          headers.forEach((h) => {
            const key = isArray ? h.col : h.name;
            h.value += isNaN(parseFloat(row[key]?.text)) ? 0 : parseFloat(row[key]?.text);
          });

          itemRowsCalc += doesRowHaveData(row, isArray) ? 1 : 0;
        });

        headers.forEach((h) => {
          h.update(h.value);
        });

        setItemRows(itemRowsCalc);

        const uniquePairs = getHeaderValues(state.rows, 'EPO');
        setItemHeaders(uniquePairs ?? 0);
      };

      ref.current.excelDispatch({ type: 'setOnUpdate', detail: sumsFunction });
    }, []);

    useEffect(() => {
      itemRows >= 1 ? setShowBulkBtn(true) : setShowBulkBtn(false);
    }, [itemRows]);
    const isEditable = useMemo(() => {
      return canSave && sfdSubmitted[0] === 0 && sdiSubmitted[0] === 0 && eadSubmitted[0] === 0 && !loadIsReadOnly;
    }, [canSave, sfdSubmitted, sdiSubmitted, eadSubmitted, loadIsReadOnly]);

    useEffect(() => {
      if (invoiceDetails?.length > 0 && pageNo >= 0) {
        let invoiceNo = invoiceDetails[pageNo].invoiceNumber;
        if (invoiceNo === null) {
          invoiceNo = '';
        }
        setLocalInvoiceData((prev) => {
          return {
            ...prev,
            invoiceNumber: invoiceNo,
          };
        });
      }
    }, [pageNo, invoiceDetails]);

    const validateStringLength = (value, length) => {
      setLocalInvoiceData((prev) => {
        return { ...prev, invoiceNumber: value };
      });
      if (value.length >= length) {
        setError('Invoice number Cannot exceed 35 characters.');
      } else {
        setError('');
      }
    };

    return (
      <>
        {showBulkModal && (
          <BulkOperations
            key="epoLoads"
            id="epoLoads"
            onBulkClose={onBulkClose}
            itemCount={itemRows}
            invoiceNumber={localInvoiceData?.invoiceNumber}
            excelState={ref?.current.excelState}
            excelRef={ref?.current}
            totalValue={totalValue}
          />
        )}
        <GridTotals
          other={[
            <Input
              label="Invoice No."
              name="invoiceNumber"
              type="text"
              required
              value={localInvoiceData.deleted ? '' : localInvoiceData.invoiceNumber}
              onChange={(e) => validateStringLength(e.target.value, 35)}
              disabled={!canSave || sdiSubmitted[0] === 1 || sfdSubmitted[0] === 1 || eadSubmitted[0] === 1}
              className="formattedText"
              error={error}
            />,
          ]}
          grossKG={grossMass}
          netKG={netMass}
          value={totalValue}
          itemCount={itemCount}
          rows={itemRows}
          additionalStyles={{ marginTop: '2rem', marginBottom: '1rem' }}
          itemHeaders={itemHeaders}
        />

        <div className="row-number-wrapper">
          <RowNumberColumn rowCount={itemRows} />
          <Spreadsheet defaultRows={15} editable={isEditable} visible={spreadsheetVisible} handle={ref}>
            <Spreadsheet.Column key="commcode" type="number" minWidth={12} align="center" maxLength={10}>
              HS Code
            </Spreadsheet.Column>
            <Spreadsheet.Column key="origin" type="letters" minWidth={7} align="center">
              Origin
            </Spreadsheet.Column>
            <Spreadsheet.Column key="commDesc" stretch type="text" minWidth={15}>
              Description
            </Spreadsheet.Column>
            <Spreadsheet.Column key="packageCount" type="number" minWidth={11}>
              No. Items
            </Spreadsheet.Column>
            <Spreadsheet.Column key="packageType" type="letters" minWidth={12}>
              Type
            </Spreadsheet.Column>
            <Spreadsheet.Column key="grossMass" type="decimal" minWidth={11}>
              Gross Mass
            </Spreadsheet.Column>
            <Spreadsheet.Column key="netMass" type="decimal" minWidth={11}>
              Net Mass
            </Spreadsheet.Column>
            <Spreadsheet.Column key="loadValue" type="decimal" minWidth={12}>
              Value
            </Spreadsheet.Column>
            <Spreadsheet.Column key="quantity2" type="decimal" minWidth={9}>
              Qty 2
            </Spreadsheet.Column>
            <Spreadsheet.Column key="healthCert" type="text" minWidth={24} transform={cleanseChedRef}>
              CHED Ref
            </Spreadsheet.Column>
            <Spreadsheet.Column key="catchCert" type="text" minWidth={13}>
              Catch Cert
            </Spreadsheet.Column>
            <Spreadsheet.Column key="meursing" type="number" minWidth={9}>
              Meursing
            </Spreadsheet.Column>
            <Spreadsheet.Column key="organic" type="letters" minWidth={8}>
              Organic
            </Spreadsheet.Column>
            <Spreadsheet.Column key="VAT" type="letters" minWidth={7}>
              VAT
            </Spreadsheet.Column>
            <Spreadsheet.Column key="rgr" type="text" minWidth={8}>
              RGR
            </Spreadsheet.Column>
            <Spreadsheet.Column key="preferential" type="letters" minWidth={7}>
              Pref
            </Spreadsheet.Column>
            <Spreadsheet.Column key="exciseProductType" type="text" minWidth={7} hidden>
              Product Type
            </Spreadsheet.Column>
            <Spreadsheet.Column key="exciseTaxCode" type="text" minWidth={7} hidden>
              Excise Code
            </Spreadsheet.Column>
          </Spreadsheet>
        </div>
      </>
    );
  }
);
